import { useEffect, useRef, useState } from "react"

export function useResize(target, options = {}, callback) {
	const { debounceMs, throttleMs } = options
	const isWindow = target === window // detect if we track window
	const [size, setSize] = useState(() => {
		if (isWindow) {
			return { width: window.innerWidth, height: window.innerHeight }
		}
		return { width: 0, height: 0 }
	})
	const lastSizeRef = useRef(size)
	const lastCallRef = useRef(0)
	const timeoutRef = useRef(null)
	const frameRef = useRef(null)
	useEffect(() => {
		const runCallback = (newSize) => {
			if (newSize.width === lastSizeRef.current.width &&
				newSize.height === lastSizeRef.current.height) {
					return // white_check_mark: skip if no change
				}
			lastSizeRef.current = newSize
			setSize(newSize)
			if (callback) callback(newSize)
		}
		
		let observer
		if (isWindow) {
			// --- Window resize ---
			const handleResize = () => {
				const newSize = { width: window.innerWidth, height: window.innerHeight }
				
				// debounce case
				if (debounceMs) {
					clearTimeout(timeoutRef.current)
					timeoutRef.current = setTimeout(() => runCallback(newSize), debounceMs)
					return
				}
				
				// throttle case
				if (throttleMs) {
					const now = Date.now()
					if (now - lastCallRef.current < throttleMs) return
					lastCallRef.current = now
				}
				cancelAnimationFrame(frameRef.current)
				frameRef.current = requestAnimationFrame(() => runCallback(newSize))
			}
			
			window.addEventListener("resize", handleResize)
			return () => {
				window.removeEventListener("resize", handleResize)
				cancelAnimationFrame(frameRef.current)
				clearTimeout(timeoutRef.current)
			}
		
		} else if (target?.current) {
			// --- element resize ---
			observer = new ResizeObserver((entries) => {
				const entry = entries[0]
				if (!entry) return
				const { width, height } = entry.contentRect
				const newSize = { width, height }
				// debounce case
				if (debounceMs) {
					clearTimeout(timeoutRef.current)
					timeoutRef.current = setTimeout(() => runCallback(newSize), debounceMs)
					return
				}
				// throttle case
				if (throttleMs) {
					const now = Date.now()
					if (now - lastCallRef.current < throttleMs) return
					lastCallRef.current = now
				}
				cancelAnimationFrame(frameRef.current)
				frameRef.current = requestAnimationFrame(() => runCallback(newSize))
			})
			
			observer.observe(target.current)
			
			return () => {
				observer.disconnect()
				cancelAnimationFrame(frameRef.current)
				clearTimeout(timeoutRef.current)
			}
		}
	}, [target, debounceMs, throttleMs, callback, isWindow])
  	
	return size
}
